import { useState, useMemo, useCallback, useEffect } from 'react'
import { generateUniqueId } from '../utils'

const useQuestionFilters = (initialFilters, collectId) => {
  const [questionFilterRules, setQuestionFilterRules] = useState(initialFilters.userFilterRules || [])

  useEffect(() => {
    const rulesWithUniqueIds = (initialFilters.userFilterRules || []).map(rule => {
      const updatedRule = { ...rule }
      if (updatedRule.rule?.conditions) {
        updatedRule.rule = { ...updatedRule.rule }
        updatedRule.rule.conditions = updatedRule.rule.conditions.map(condition => ({
          ...condition,
          uniqueId: condition.uniqueId || generateUniqueId('condition')
        }))
      }
      return updatedRule
    })
    setQuestionFilterRules(rulesWithUniqueIds)
  }, [initialFilters.userFilterRules])

  const activeQuestionFilterRules = useMemo(() =>
    questionFilterRules.filter(rule => !rule.shouldDestroy), [questionFilterRules])

  const activeQuestionFilters = useMemo(() => {
    const filters = []

    activeQuestionFilterRules.forEach(userFilterRule => {
      if (userFilterRule.rule?.conditions) {
        userFilterRule.rule.conditions.forEach(condition => {
          if (condition.targetableType === 'Question' && !condition.shouldDestroy) {
            let values = []
            if (condition.value !== null) {
              const splitValues = condition.value.split(';')
              const hasEmptyValue = splitValues.includes('')
              values = splitValues.filter(value => value !== '').concat(hasEmptyValue ? ['__EMPTY__'] : [])
            }

            filters.push({
              id: condition.id,
              questionId: condition.targetableId?.toString(),
              state: condition.state,
              values,
              uniqueId: condition.uniqueId
            })
          }
        })
      }
    })

    return filters
  }, [activeQuestionFilterRules])

  const addCondition = useCallback((targetableId, state, value) => {
    setQuestionFilterRules(prev => {
      const [existingRule] = prev
      if (existingRule) {
        const updatedRule = { ...existingRule }
        updatedRule.shouldDestroy = false
        updatedRule.rule = { ...updatedRule.rule }
        updatedRule.rule.conditions = [...updatedRule.rule.conditions || []]

        const newCondition = {
          targetableId,
          targetableType: 'Question',
          state,
          value,
          uniqueId: generateUniqueId('condition')
        }

        updatedRule.rule.conditions.push(newCondition)
        return [updatedRule]
      }
      const newRule = {
        collectId,
        rule: {
          operator: 'every',
          conditions: [{
            targetableId,
            targetableType: 'Question',
            state,
            value,
            uniqueId: generateUniqueId('condition')
          }]
        },
        shouldDestroy: false
      }
      return [newRule]
    })
  }, [collectId])

  const removeCondition = useCallback(uniqueId => {
    setQuestionFilterRules(prev => {
      if (!prev[0])
        return prev

      const [rule] = prev
      const updatedRule = { ...rule }
      updatedRule.rule = { ...updatedRule.rule }
      updatedRule.rule.conditions = [...updatedRule.rule.conditions || []]

      const conditionIndex = updatedRule.rule.conditions.findIndex(cond =>
        cond.uniqueId === uniqueId
      )

      if (conditionIndex === -1)
        return prev

      const targetCondition = updatedRule.rule.conditions[conditionIndex]

      if (targetCondition.id) {
        // For existing conditions with ID, mark for destruction
        updatedRule.rule.conditions[conditionIndex] = {
          ...targetCondition,
          shouldDestroy: true
        }
      } else {
        // For new conditions without ID, remove completely
        updatedRule.rule.conditions.splice(conditionIndex, 1)
      }

      // If no active conditions remain, mark the entire rule for destruction
      const hasActiveConditions = updatedRule.rule.conditions.some(cond =>
        !cond.shouldDestroy
      )

      if (!hasActiveConditions) {
        if (updatedRule.id)
          updatedRule.shouldDestroy = true
        else
          return []
      }

      return [updatedRule]
    })
  }, [])

  const updateCondition = useCallback((uniqueId, updates) => {
    setQuestionFilterRules(prev => {
      if (!prev[0])
        return prev

      const [firstRule] = prev
      const rule = { ...firstRule }
      rule.rule = { ...rule.rule }
      rule.rule.conditions = [...rule.rule.conditions || []]

      const conditionIndex = rule.rule.conditions.findIndex(condition =>
        condition.uniqueId === uniqueId
      )

      if (conditionIndex === -1)
        return prev

      rule.rule.conditions[conditionIndex] = {
        ...rule.rule.conditions[conditionIndex],
        ...updates
      }

      return [rule]
    })
  }, [collectId])

  const clearQuestionFilterRules = useCallback(() => {
    setQuestionFilterRules(prev =>
      prev.map(rule => {
        if (rule.id) {
          const updatedRule = { ...rule, shouldDestroy: true }
          return updatedRule
        }
        return null
      }).filter(Boolean)
    )
  }, [])

  const addQuestionFilter = useCallback(() => {
    addCondition(null, 'in', null)
  }, [addCondition])

  const removeQuestionFilter = useCallback(uniqueId => {
    removeCondition(uniqueId)
  }, [removeCondition])

  const updateQuestionFilter = useCallback((uniqueId, field, value) => {
    const filter = activeQuestionFilters.find(filterItem => filterItem.uniqueId === uniqueId)
    if (filter) {
      if (field === 'questionId') {
        updateCondition(filter.uniqueId, {
          targetableId: parseInt(value, 10),
          targetableType: 'Question',
          // Reset values when changing question
          state: 'in',
          value: null
        })
      } else if (field === 'state')
        updateCondition(filter.uniqueId, { [field]: value })
    }
  }, [activeQuestionFilters, updateCondition])

  const updateQuestionValues = useCallback((uniqueId, values) => {
    const filter = activeQuestionFilters.find(filterItem => filterItem.uniqueId === uniqueId)
    if (filter) {
      // Transform UI values to backend format while preserving order
      let backendValue = null

      if (values.length > 0) {
        const transformedValues = values.map(value => value === '__EMPTY__' ? '' : value)
        backendValue = transformedValues.join(';')
      }

      updateCondition(filter.uniqueId, {
        state: 'in',
        value: backendValue
      })
    }
  }, [activeQuestionFilters, updateCondition])

  return {
    questionFilterRules,
    activeQuestionFilterRules,
    activeQuestionFilters,
    clearQuestionFilterRules,
    addQuestionFilter,
    removeQuestionFilter,
    updateQuestionFilter,
    updateQuestionValues
  }
}

export default useQuestionFilters